import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios, { getToken, setToken } from '../utils/axiosInstance';

// Create authentication context
const AuthContext = createContext();

// Custom hook to use authentication context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Authentication provider component, backed by the emailed-code login.
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(() => !!getToken());
  const [channel, setChannel] = useState(null);

  // Fetch the current user's profile from the backend.
  const fetchUserDetails = useCallback(async () => {
    try {
      const response = await axios.get('api/auth/profile/');
      setUser(response.data.user || response.data);
    } catch (e) {
      console.error('Error fetching user details:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (token) => {
    setToken(token);
    await fetchUserDetails();
    channel?.postMessage({ type: 'login' });
  }, [channel, fetchUserDetails]);

  const logout = useCallback((sendMessage = true) => {
    setToken(null);
    setUser(null);
    setLoading(false);
    if (sendMessage) channel?.postMessage({ type: 'logout' });
  }, [channel]);

  // Pick up an existing session when the app opens.
  useEffect(() => {
    if (getToken()) fetchUserDetails();
  }, [fetchUserDetails]);

  // Cross-tab sync.
  useEffect(() => {
    const authChannel = new BroadcastChannel('auth');
    authChannel.onmessage = (event) => {
      if (event.data.type === 'logout') {
        setUser(null);
      } else if (event.data.type === 'login') {
        fetchUserDetails();
      }
    };
    setChannel(authChannel);
    return () => authChannel.close();
  }, [fetchUserDetails]);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      logout,
      isAuthenticated: !!user,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
